const REQUIRED_FIREBASE_ENV = {
  apiKey: "REACT_APP_FIREBASE_API_KEY",
  authDomain: "REACT_APP_FIREBASE_AUTH_DOMAIN",
  projectId: "REACT_APP_FIREBASE_PROJECT_ID",
  storageBucket: "REACT_APP_FIREBASE_STORAGE_BUCKET",
  messagingSenderId: "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
  appId: "REACT_APP_FIREBASE_APP_ID",
  recaptchaSiteKey: "REACT_APP_RECAPTCHA_SITE_KEY",
};

const APPCHECK_PROVIDER_ENV = "REACT_APP_APPCHECK_PROVIDER";
const SUPPORTED_APPCHECK_PROVIDERS = new Set(["enterprise", "v3"]);
const FIREBASE_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;
const STAGING_FIREBASE_MEASUREMENT_ID = "G-7023390L5V";
const {validateBrowserIdentity} = require("./browserIdentity.active.js");
const {storefrontMediaPresentationEnabled} = require("./storefrontMediaPresentationFeature");

function value(env, key) {
  return String(env?.[key] || "").trim();
}

function flag(env, key) {
  return /^(1|true|yes)$/i.test(value(env, key));
}

function normalizeDeploymentEnvironment(raw, nodeEnv) {
  const selected = String(raw || "").trim().toLowerCase();
  if (!selected && (nodeEnv === "development" || nodeEnv === "test")) return "development";
  if (["development", "staging", "production"].includes(selected)) return selected;
  throw new Error(
    "REACT_APP_DEPLOY_ENV must explicitly be development, staging, or production.",
  );
}

function normalizeAppCheckProvider(raw, deploymentEnvironment) {
  const selected = String(raw || "").trim().toLowerCase();
  if (!selected) {
    const stagingRequirement = deploymentEnvironment === "staging" ?
      " Staging must explicitly use enterprise and cannot fall back to v3." : "";
    throw new Error(
      `${APPCHECK_PROVIDER_ENV} must explicitly be enterprise or v3.${stagingRequirement}`,
    );
  }
  if (!SUPPORTED_APPCHECK_PROVIDERS.has(selected)) {
    throw new Error(`${APPCHECK_PROVIDER_ENV} must be enterprise or v3.`);
  }
  if (deploymentEnvironment === "staging" && selected !== "enterprise") {
    throw new Error("A staging build must use the enterprise App Check provider.");
  }
  return selected;
}

function buildRuntimeConfig(env = {}) {
  const nodeEnv = value(env, "NODE_ENV");
  const deploymentEnvironment = normalizeDeploymentEnvironment(
    value(env, "REACT_APP_DEPLOY_ENV"),
    nodeEnv,
  );
  const appCheckProvider = normalizeAppCheckProvider(
    value(env, APPCHECK_PROVIDER_ENV),
    deploymentEnvironment,
  );

  const missing = Object.values(REQUIRED_FIREBASE_ENV).filter((key) => !value(env, key));
  if (missing.length) {
    throw new Error(`Missing required Firebase environment values: ${missing.join(", ")}`);
  }

  const siteOriginValue = value(env, "REACT_APP_LIVE_URL");
  if (!siteOriginValue) {
    throw new Error("REACT_APP_LIVE_URL is required.");
  }
  let siteOrigin;
  try {
    const parsed = new URL(siteOriginValue);
    const localDevelopment = deploymentEnvironment === "development" &&
      ["localhost", "127.0.0.1"].includes(parsed.hostname);
    if (parsed.protocol !== "https:" && !(localDevelopment && parsed.protocol === "http:")) {
      throw new Error("invalid protocol");
    }
    siteOrigin = parsed.origin;
  } catch {
    throw new Error("REACT_APP_LIVE_URL must be a valid HTTPS origin.");
  }

  const firebaseConfig = {};
  for (const [configKey, envKey] of Object.entries(REQUIRED_FIREBASE_ENV)) {
    if (configKey !== "recaptchaSiteKey") firebaseConfig[configKey] = value(env, envKey);
  }
  const measurementId = value(env, "REACT_APP_FIREBASE_MEASUREMENT_ID");
  if (measurementId && !FIREBASE_MEASUREMENT_ID_PATTERN.test(measurementId)) {
    throw new Error("REACT_APP_FIREBASE_MEASUREMENT_ID must be a valid G- measurement ID.");
  }
  if (measurementId) firebaseConfig.measurementId = measurementId;

  const productionProjectId = value(env, "REACT_APP_PRODUCTION_FIREBASE_PROJECT_ID");
  const productionStorageBucket = value(
    env,
    "REACT_APP_PRODUCTION_FIREBASE_STORAGE_BUCKET",
  );
  if (deploymentEnvironment === "staging") {
    if (!measurementId || measurementId.toLowerCase() === "undefined") {
      throw new Error("A staging build requires a Firebase measurement ID.");
    }
    if (measurementId !== STAGING_FIREBASE_MEASUREMENT_ID) {
      throw new Error("The staging Firebase measurement ID does not match the expected property.");
    }
    if (!productionProjectId || !productionStorageBucket) {
      throw new Error(
        "Production project and Storage bucket guards are required for staging isolation checks.",
      );
    }
    if (firebaseConfig.projectId === productionProjectId) {
      throw new Error("A staging build cannot use the configured production Firebase project.");
    }
    if (firebaseConfig.storageBucket === productionStorageBucket) {
      throw new Error("A staging build cannot use the configured production Storage bucket.");
    }
    if (firebaseConfig.authDomain !== `${firebaseConfig.projectId}.firebaseapp.com`) {
      throw new Error("A staging build must use the matching staging Firebase Auth domain.");
    }
  }
  if (
    deploymentEnvironment === "production" &&
    productionProjectId &&
    firebaseConfig.projectId !== productionProjectId
  ) {
    throw new Error("The production build Firebase project does not match the expected project.");
  }
  if (
    deploymentEnvironment === "production" &&
    productionStorageBucket &&
    firebaseConfig.storageBucket !== productionStorageBucket
  ) {
    throw new Error("The production build Storage bucket does not match the expected bucket.");
  }
  validateBrowserIdentity({
    ...firebaseConfig,
    appCheckProvider,
    recaptchaSiteKey: value(env, "REACT_APP_RECAPTCHA_SITE_KEY"),
    functionsRegion: value(env, "REACT_APP_FIREBASE_FUNCTIONS_REGION") || "us-east1",
    siteOrigin,
  }, deploymentEnvironment);

  const productionContentStudioCore = deploymentEnvironment === "production" &&
    flag(env, "REACT_APP_PRODUCTION_CONTENT_STUDIO_CORE");
  const productionStorefrontMediaPresentation = storefrontMediaPresentationEnabled({
    deploymentEnvironment,
    sentinel: value(env, "REACT_APP_PRODUCTION_STOREFRONT_MEDIA_PRESENTATION"),
  });
  const contentStudioSurfaceMode = deploymentEnvironment === "staging" ? "full" :
    productionContentStudioCore ? "production-core" : "disabled";

  const useEmulators = flag(env, "REACT_APP_USE_FIREBASE_EMULATORS");
  if (useEmulators && deploymentEnvironment !== "development") {
    throw new Error("Firebase emulators may only be enabled for a development build.");
  }

  const privatePreviewProxyEnabled = value(env, "REACT_APP_BEATMATCH_PRIVATE_PREVIEW_PROXY") ===
    "reviewed-enabled-v1";
  const functionsBaseUrlOverride = value(env, "REACT_APP_FUNCTIONS_BASE_URL");
  if (privatePreviewProxyEnabled) {
    if (nodeEnv !== "development" || deploymentEnvironment !== "staging" ||
        functionsBaseUrlOverride !== "http://127.0.0.1:3000/__beatmatch_staging_functions") {
      throw new Error("The BeatMatch private preview proxy is restricted to the reviewed local staging endpoint.");
    }
  } else if (functionsBaseUrlOverride) {
    throw new Error("A Functions base URL override requires the reviewed local BeatMatch preview sentinel.");
  }

  const appCheckDebugRequested = flag(env, "REACT_APP_APP_CHECK_DEBUG");
  const appCheckDebugToken = value(env, "REACT_APP_APP_CHECK_DEBUG_TOKEN");
  const reviewedLocalPreviewDebug = privatePreviewProxyEnabled && nodeEnv === "development" &&
    deploymentEnvironment === "staging" && functionsBaseUrlOverride ===
      "http://127.0.0.1:3000/__beatmatch_staging_functions" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(appCheckDebugToken);
  if ((appCheckDebugRequested || appCheckDebugToken) && !useEmulators && !reviewedLocalPreviewDebug) {
    throw new Error("App Check debug mode requires emulators or the reviewed localhost staging preview with a token.");
  }
  if (privatePreviewProxyEnabled && !appCheckDebugToken) {
    throw new Error("The localhost BeatMatch preview requires a registered staging App Check debug token.");
  }

  return {
    deploymentEnvironment,
    appCheckProvider,
    productionContentStudioCore,
    productionStorefrontMediaPresentation,
    contentStudioSurfaceMode,
    siteOrigin,
    firebaseConfig,
    recaptchaSiteKey: value(env, "REACT_APP_RECAPTCHA_SITE_KEY"),
    region: value(env, "REACT_APP_FIREBASE_FUNCTIONS_REGION") || "us-east1",
    useEmulators,
    emulatorHost: value(env, "REACT_APP_FIREBASE_EMULATOR_HOST") || "127.0.0.1",
    emulatorPorts: {
      auth: Number(value(env, "REACT_APP_FIREBASE_AUTH_EMULATOR_PORT") || 9099),
      firestore: Number(value(env, "REACT_APP_FIRESTORE_EMULATOR_PORT") || 8080),
      functions: Number(value(env, "REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT") || 5001),
      storage: Number(value(env, "REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT") || 9199),
    },
    appCheckDebugToken: appCheckDebugToken || (appCheckDebugRequested ? true : null),
    functionsBaseUrlOverride: privatePreviewProxyEnabled ? functionsBaseUrlOverride : "",
  };
}

function getFunctionBaseUrl(config) {
  if (config.functionsBaseUrlOverride) return config.functionsBaseUrlOverride;
  if (config.useEmulators) {
    return `http://${config.emulatorHost}:${config.emulatorPorts.functions}/` +
      `${config.firebaseConfig.projectId}/${config.region}`;
  }
  return `https://${config.region}-${config.firebaseConfig.projectId}.cloudfunctions.net`;
}

function getFunctionEndpoint(config, functionName) {
  const name = String(functionName || "").trim();
  if (!name || !/^[A-Za-z0-9_-]+$/.test(name)) {
    throw new Error("A valid Firebase Function name is required.");
  }
  return `${getFunctionBaseUrl(config)}/${name}`;
}

export {
  APPCHECK_PROVIDER_ENV,
  REQUIRED_FIREBASE_ENV,
  SUPPORTED_APPCHECK_PROVIDERS,
  buildRuntimeConfig,
  getFunctionBaseUrl,
  getFunctionEndpoint,
  normalizeAppCheckProvider,
  normalizeDeploymentEnvironment,
};
